import React, { useContext, useMemo, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    Box,
    Typography,
    Checkbox,
    FormControlLabel,
    Divider,
    Button,
    IconButton,
    Tooltip,
    Paper,
    Grid,
    Chip,
    Collapse,
    Card,
    CardHeader,
    CardContent,
    Stack,
    List,
    ListItemButton,
    ListItemAvatar,
    ListItemText,
    Avatar,
    TextField,
    InputAdornment,
    CircularProgress,
    Alert,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { toast } from 'react-toastify';

import { PermissionContext } from '../../../../context/PermissionContext';
import { PERMISSION_MAP } from '../../../../context/Mappings/permissionMap';
import { API_BASE_URL_SECURITY } from '../../../../utils/config';

const GROUP_LABELS = {
    cosecha: 'Cosecha',
    herbicidas: 'Herbicidas',
    madurantes: 'Madurantes',
    variedades: 'Variedades',
    plagas: 'Plagas',
    riego: 'Riego',
    _otros: 'Otros',
};

const labelFromGroupKey = (k) =>
    GROUP_LABELS[k] || (k ? k.charAt(0).toUpperCase() + k.slice(1) : 'Otros');

const PermissionControl = () => {
    const { openPermissionUI } = useContext(PermissionContext);

    // --- Estado de usuarios (panel izquierdo)
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [userQuery, setUserQuery] = useState('');
    const [selectedUserId, setSelectedUserId] = useState(null);

    const selectedUser = useMemo(
        () => users.find((u) => u._id === selectedUserId) || null,
        [users, selectedUserId]
    );

    // --- Estado de permisos del usuario seleccionado
    const [loadingPerms, setLoadingPerms] = useState(false);
    const [draft, setDraft] = useState([]); // [{moduleKey, submoduleKey|null, canView}]

    // --- Estado UI de grupos (expandido/colapsado)
    const [openGroups, setOpenGroups] = useState({}); // key: `${moduleKey}:${groupKey}` → bool

    const toggleGroupOpen = useCallback((moduleKey, groupKey) => {
        setOpenGroups((prev) => {
            const id = `${moduleKey}:${groupKey}`;
            return { ...prev, [id]: !(prev[id] ?? true) };
        });
    }, []);

    // ===== Helpers =====
    const normalizePerms = useCallback((perms) => {
        return Array.isArray(perms)
            ? perms.map((p) => ({
                moduleKey: p.moduleKey,
                submoduleKey: p.submoduleKey ?? null,
                canView: !!p.canView,
            }))
            : [];
    }, []);

    const isChecked = useCallback(
        (moduleKey, submoduleKey = null) => {
            const found = draft.find(
                (d) =>
                    d.moduleKey === moduleKey &&
                    (submoduleKey === null
                        ? d.submoduleKey === null
                        : d.submoduleKey === submoduleKey)
            );
            return !!(found && found.canView);
        },
        [draft]
    );

    const setChecked = (moduleKey, submoduleKey = null, value = true) => {
        setDraft((prev) => {
            const idx = prev.findIndex(
                (d) =>
                    d.moduleKey === moduleKey &&
                    (submoduleKey === null
                        ? d.submoduleKey === null
                        : d.submoduleKey === submoduleKey)
            );
            const copy = [...prev];
            if (idx >= 0) copy[idx] = { ...copy[idx], canView: value };
            else copy.push({ moduleKey, submoduleKey, canView: value });
            return copy;
        });
    };

    const toggleModule = (moduleKey, checked) => {
        setChecked(moduleKey, null, checked);
        const module = PERMISSION_MAP.find((m) => m.moduleKey === moduleKey);
        if (module?.submodules?.length) {
            if (!checked) {
                module.submodules.forEach((s) =>
                    setChecked(moduleKey, s.submoduleKey, false)
                );
            }
        }
    };

    const toggleSubmodule = (moduleKey, submoduleKey, checked) => {
        if (checked && !isChecked(moduleKey, null)) setChecked(moduleKey, null, true);
        setChecked(moduleKey, submoduleKey, checked);
    };

    // --- Toggle por grupo (seleccionar/limpiar todo el grupo)
    const toggleGroup = (moduleKey, items, checked) => {
        if (checked && !isChecked(moduleKey, null)) setChecked(moduleKey, null, true);
        items.forEach((s) => setChecked(moduleKey, s.submoduleKey, checked));
    };

    // Contador por módulo
    const subCount = useMemo(() => {
        const map = {};
        PERMISSION_MAP.forEach((m) => {
            const total = m.submodules?.length || 0;
            const selected = (m.submodules || []).filter((s) =>
                isChecked(m.moduleKey, s.submoduleKey)
            ).length;
            map[m.moduleKey] = { total, selected };
        });
        return map;
    }, [isChecked]); // <- aquí ya no está "draft"

    const selectAll = (mod) => {
        setChecked(mod.moduleKey, null, true);
        (mod.submodules || []).forEach((s) =>
            setChecked(mod.moduleKey, s.submoduleKey, true)
        );
    };

    const clearAll = (mod) => {
        setChecked(mod.moduleKey, null, false);
        (mod.submodules || []).forEach((s) =>
            setChecked(mod.moduleKey, s.submoduleKey, false)
        );
    };

    // ===== Agrupación de submódulos (sin cambiar PERMISSION_MAP)
    const buildGroups = useCallback((submodules) => {
        const groups = {};
        (submodules || []).forEach((s) => {
            const key = s.submoduleKey || '';
            const parts = key.split('.');
            const gk = parts.length > 1 ? parts[0] : '_otros';
            if (!groups[gk]) groups[gk] = [];
            groups[gk].push(s);
        });
        return groups;
    }, []);

    // ===== Cargas =====
    const fetchUsers = useCallback(async () => {
        try {
            setLoadingUsers(true);
            const res = await axios.get(`${API_BASE_URL_SECURITY}usuarios`);
            const list = Array.isArray(res.data) ? res.data : [];
            setUsers(list);
            if (list.length && !selectedUserId) setSelectedUserId(list[0]._id);
        } catch (err) {
            console.error(err);
            toast.error('No se pudo cargar el listado de usuarios');
        } finally {
            setLoadingUsers(false);
        }
    }, [selectedUserId]);

    const fetchPermissionsForUser = useCallback(
        async (userId) => {
            if (!userId) return;
            try {
                setLoadingPerms(true);
                const res = await axios.get(
                    `${API_BASE_URL_SECURITY}permissions/${userId}`
                );
                setDraft(normalizePerms(res.data));
            } catch (err) {
                console.error(err);
                toast.error('No se pudieron cargar los permisos del usuario');
                setDraft([]);
            } finally {
                setLoadingPerms(false);
            }
        },
        [normalizePerms]
    );

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    useEffect(() => {
        fetchPermissionsForUser(selectedUserId);
    }, [selectedUserId, fetchPermissionsForUser]);

    // ===== Guardar =====
    const handleSave = async () => {
        if (!selectedUserId) {
            toast.info('Selecciona un usuario primero');
            return;
        }
        try {
            const compact = [];
            const seen = new Set();
            for (const d of draft) {
                const key = `${d.moduleKey}::${d.submoduleKey ?? 'null'}`;
                if (seen.has(key)) continue;
                seen.add(key);
                compact.push({
                    moduleKey: d.moduleKey,
                    submoduleKey: d.submoduleKey ?? null,
                    canView: !!d.canView,
                });
            }
            await axios.put(
                `${API_BASE_URL_SECURITY}permissions/${selectedUserId}`,
                {
                    permissions: compact,
                }
            );
            toast.success('Permisos guardados para el usuario seleccionado');
            fetchPermissionsForUser(selectedUserId);
        } catch (e) {
            console.error(e);
            toast.error('Error al guardar permisos');
        }
    };

    // ===== Filtros de usuarios =====
    const filteredUsers = useMemo(() => {
        const q = userQuery.trim().toLowerCase();
        if (!q) return users;
        return users.filter((u) => {
            const full = `${u.NOMBRE || ''} ${u.APELLIDO || ''}`
                .trim()
                .toLowerCase();
            return (
                full.includes(q) ||
                (u.EMAIL || '').toLowerCase().includes(q)
            );
        });
    }, [users, userQuery]);

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                    Control de Permisos por Usuario
                </Typography>
                <Tooltip title="Abrir selector de permisos desde cualquier pantalla">
                    <IconButton onClick={openPermissionUI} size="small">
                        <SettingsIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <Grid container spacing={2}>
                {/* Panel izquierdo: listado de usuarios */}
                <Grid item xs={12} md={4} lg={3}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            borderRadius: 3,
                            border: (theme) =>
                                `1px solid ${theme.palette.divider}`,
                            height: { md: 'calc(100vh - 210px)' },
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                        }}
                    >
                        <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 700 }}
                        >
                            Usuarios
                        </Typography>

                        <TextField
                            size="small"
                            placeholder="Buscar por nombre o email"
                            value={userQuery}
                            onChange={(e) => setUserQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Box sx={{ flex: 1, overflowY: 'auto', pr: 1 }}>
                            {loadingUsers ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        mt: 4,
                                    }}
                                >
                                    <CircularProgress size={28} />
                                </Box>
                            ) : filteredUsers.length === 0 ? (
                                <Alert severity="info" sx={{ mt: 1 }}>
                                    No hay usuarios que coincidan con la
                                    búsqueda.
                                </Alert>
                            ) : (
                                <List dense disablePadding>
                                    {filteredUsers.map((u) => {
                                        const fullName = `${u.NOMBRE || ''} ${
                                            u.APELLIDO || ''
                                        }`.trim();
                                        return (
                                            <ListItemButton
                                                key={u._id}
                                                selected={
                                                    u._id === selectedUserId
                                                }
                                                onClick={() =>
                                                    setSelectedUserId(u._id)
                                                }
                                                sx={{
                                                    borderRadius: 2,
                                                    mb: 0.5,
                                                }}
                                            >
                                                <ListItemAvatar>
                                                    <Avatar>
                                                        <PersonIcon />
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={
                                                        fullName || u.EMAIL
                                                    }
                                                    secondary={u.EMAIL}
                                                    primaryTypographyProps={{
                                                        noWrap: true,
                                                    }}
                                                    secondaryTypographyProps={{
                                                        noWrap: true,
                                                    }}
                                                />
                                            </ListItemButton>
                                        );
                                    })}
                                </List>
                            )}
                        </Box>
                    </Paper>
                </Grid>

                {/* Panel derecho: permisos del usuario seleccionado */}
                <Grid item xs={12} md={8} lg={9}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 1, md: 2 },
                            borderRadius: 3,
                            border: (theme) =>
                                `1px solid ${theme.palette.divider}`,
                            background:
                                'linear-gradient(180deg, rgba(0,0,0,0.015) 0%, rgba(0,0,0,0.03) 100%)',
                            minHeight: 320,
                        }}
                    >
                        {!selectedUser ? (
                            <Box sx={{ p: 3 }}>
                                <Alert severity="info">
                                    Selecciona un usuario en la lista para
                                    visualizar y editar sus permisos.
                                </Alert>
                            </Box>
                        ) : (
                            <>
                                <Box
                                    sx={{
                                        px: 1,
                                        pb: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        flexWrap: 'wrap',
                                        gap: 1,
                                    }}
                                >
                                    <Typography
                                        variant="subtitle1"
                                        sx={{ fontWeight: 700 }}
                                    >
                                        Permisos de:{' '}
                                        {`${selectedUser.NOMBRE || ''} ${
                                            selectedUser.APELLIDO || ''
                                        }`.trim() || selectedUser.EMAIL}
                                    </Typography>
                                    {loadingPerms && (
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            gap={1}
                                        >
                                            <CircularProgress size={20} />
                                            <Typography variant="body2">
                                                Cargando permisos…
                                            </Typography>
                                        </Stack>
                                    )}
                                </Box>

                                <Grid container spacing={2}>
                                    {PERMISSION_MAP.map((mod) => {
                                        const moduleChecked = isChecked(
                                            mod.moduleKey,
                                            null
                                        );
                                        const counts =
                                            subCount[mod.moduleKey] || {
                                                total: 0,
                                                selected: 0,
                                            };
                                        const groups = buildGroups(
                                            mod.submodules
                                        );

                                        // Detecta si vale la pena mostrar grupos (si hay al menos un submoduleKey con ".")
                                        const hasGroups = (
                                            mod.submodules || []
                                        ).some((s) =>
                                            (s.submoduleKey || '').includes('.')
                                        );

                                        return (
                                            <Grid
                                                item
                                                xs={12}
                                                key={mod.moduleKey}
                                            >
                                                <Card
                                                    variant="outlined"
                                                    sx={{
                                                        borderRadius: 3,
                                                        overflow: 'hidden',
                                                        borderColor: 'divider',
                                                    }}
                                                >
                                                    <CardHeader
                                                        sx={{
                                                            '& .MuiCardHeader-content':
                                                                {
                                                                    overflow:
                                                                        'hidden',
                                                                },
                                                            backgroundColor:
                                                                moduleChecked
                                                                    ? 'action.hover'
                                                                    : 'background.paper',
                                                            py: 1.5,
                                                        }}
                                                        title={
                                                            <Box
                                                                sx={{
                                                                    display:
                                                                        'flex',
                                                                    alignItems:
                                                                        'center',
                                                                    justifyContent:
                                                                        'space-between',
                                                                    gap: 2,
                                                                }}
                                                            >
                                                                <FormControlLabel
                                                                    sx={{
                                                                        m: 0,
                                                                    }}
                                                                    control={
                                                                        <Checkbox
                                                                            checked={
                                                                                moduleChecked
                                                                            }
                                                                            onChange={(
                                                                                e
                                                                            ) =>
                                                                                toggleModule(
                                                                                    mod.moduleKey,
                                                                                    e
                                                                                        .target
                                                                                        .checked
                                                                                )
                                                                            }
                                                                        />
                                                                    }
                                                                    label={
                                                                        <Typography
                                                                            variant="h6"
                                                                            sx={{
                                                                                fontWeight: 700,
                                                                            }}
                                                                        >
                                                                            {
                                                                                mod.label
                                                                            }
                                                                        </Typography>
                                                                    }
                                                                />

                                                                <Stack
                                                                    direction="row"
                                                                    spacing={1}
                                                                    alignItems="center"
                                                                >
                                                                    {counts.total >
                                                                        0 && (
                                                                            <Chip
                                                                                size="small"
                                                                                label={`${counts.selected}/${counts.total} submódulos`}
                                                                                variant={
                                                                                    moduleChecked
                                                                                        ? 'filled'
                                                                                        : 'outlined'
                                                                                }
                                                                            />
                                                                        )}
                                                                    {mod
                                                                            .submodules
                                                                            ?.length >
                                                                        0 && (
                                                                            <>
                                                                                <Button
                                                                                    size="small"
                                                                                    onClick={() =>
                                                                                        selectAll(
                                                                                            mod
                                                                                        )
                                                                                    }
                                                                                    disabled={
                                                                                        !mod
                                                                                            .submodules
                                                                                            ?.length
                                                                                    }
                                                                                >
                                                                                    Seleccionar
                                                                                    todo
                                                                                </Button>
                                                                                <Button
                                                                                    size="small"
                                                                                    color="inherit"
                                                                                    onClick={() =>
                                                                                        clearAll(
                                                                                            mod
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    Limpiar
                                                                                </Button>
                                                                            </>
                                                                        )}
                                                                </Stack>
                                                            </Box>
                                                        }
                                                    />

                                                    {/* Submódulos visibles solo si el módulo está activo */}
                                                    {mod.submodules?.length >
                                                        0 && (
                                                            <Collapse
                                                                in={moduleChecked}
                                                                timeout="auto"
                                                                unmountOnExit
                                                            >
                                                                <Divider />
                                                                <CardContent
                                                                    sx={{ pt: 2 }}
                                                                >
                                                                    {!hasGroups ? (
                                                                        // Render plano (módulos sin jerarquía)
                                                                        <Grid container spacing={1.5}>
                                                                            {mod.submodules.map(
                                                                                (
                                                                                    s
                                                                                ) => (
                                                                                    <Grid
                                                                                        item
                                                                                        xs={
                                                                                            12
                                                                                        }
                                                                                        sm={
                                                                                            6
                                                                                        }
                                                                                        md={
                                                                                            4
                                                                                        }
                                                                                        lg={
                                                                                            3
                                                                                        }
                                                                                        key={
                                                                                            s.submoduleKey
                                                                                        }
                                                                                    >
                                                                                        <FormControlLabel
                                                                                            control={
                                                                                                <Checkbox
                                                                                                    checked={isChecked(
                                                                                                        mod.moduleKey,
                                                                                                        s.submoduleKey
                                                                                                    )}
                                                                                                    onChange={(
                                                                                                        e
                                                                                                    ) =>
                                                                                                        toggleSubmodule(
                                                                                                            mod.moduleKey,
                                                                                                            s.submoduleKey,
                                                                                                            e
                                                                                                                .target
                                                                                                                .checked
                                                                                                        )
                                                                                                    }
                                                                                                />
                                                                                            }
                                                                                            label={
                                                                                                <Typography variant="body2">
                                                                                                    {
                                                                                                        s.label
                                                                                                    }
                                                                                                </Typography>
                                                                                            }
                                                                                        />
                                                                                    </Grid>
                                                                                )
                                                                            )}
                                                                        </Grid>
                                                                    ) : (
                                                                        // Render agrupado (SIG Agrícola)
                                                                        <Stack spacing={2}>
                                                                            {Object.entries(
                                                                                groups
                                                                            ).map(
                                                                                ([
                                                                                     groupKey,
                                                                                     items,
                                                                                 ]) => {
                                                                                    const total =
                                                                                        items.length;
                                                                                    const selected =
                                                                                        items.filter(
                                                                                            (
                                                                                                s
                                                                                            ) =>
                                                                                                isChecked(
                                                                                                    mod.moduleKey,
                                                                                                    s.submoduleKey
                                                                                                )
                                                                                        )
                                                                                            .length;
                                                                                    const groupChecked =
                                                                                        total >
                                                                                        0 &&
                                                                                        selected ===
                                                                                        total;
                                                                                    const groupIndeterminate =
                                                                                        selected >
                                                                                        0 &&
                                                                                        selected <
                                                                                        total;
                                                                                    const open =
                                                                                        openGroups[
                                                                                            `${mod.moduleKey}:${groupKey}`
                                                                                            ] ??
                                                                                        true;

                                                                                    return (
                                                                                        <Box
                                                                                            key={`${mod.moduleKey}-${groupKey}`}
                                                                                            sx={{
                                                                                                border: '1px solid',
                                                                                                borderColor:
                                                                                                    'divider',
                                                                                                borderRadius: 2,
                                                                                            }}
                                                                                        >
                                                                                            <Box
                                                                                                sx={{
                                                                                                    display:
                                                                                                        'flex',
                                                                                                    alignItems:
                                                                                                        'center',
                                                                                                    justifyContent:
                                                                                                        'space-between',
                                                                                                    px: 1,
                                                                                                    py: 0.5,
                                                                                                    backgroundColor:
                                                                                                        'action.hover',
                                                                                                    borderTopLeftRadius: 8,
                                                                                                    borderTopRightRadius: 8,
                                                                                                }}
                                                                                            >
                                                                                                <FormControlLabel
                                                                                                    sx={{
                                                                                                        m: 0,
                                                                                                    }}
                                                                                                    control={
                                                                                                        <Checkbox
                                                                                                            checked={
                                                                                                                groupChecked
                                                                                                            }
                                                                                                            indeterminate={
                                                                                                                groupIndeterminate
                                                                                                            }
                                                                                                            onChange={(
                                                                                                                e
                                                                                                            ) =>
                                                                                                                toggleGroup(
                                                                                                                    mod.moduleKey,
                                                                                                                    items,
                                                                                                                    e
                                                                                                                        .target
                                                                                                                        .checked
                                                                                                                )
                                                                                                            }
                                                                                                        />
                                                                                                    }
                                                                                                    label={
                                                                                                        <Typography
                                                                                                            sx={{
                                                                                                                fontWeight: 700,
                                                                                                            }}
                                                                                                        >
                                                                                                            {labelFromGroupKey(
                                                                                                                groupKey
                                                                                                            )}{' '}
                                                                                                            <Typography
                                                                                                                component="span"
                                                                                                                variant="caption"
                                                                                                                sx={{
                                                                                                                    ml: 1,
                                                                                                                }}
                                                                                                            >
                                                                                                                {
                                                                                                                    selected
                                                                                                                }
                                                                                                                /
                                                                                                                {
                                                                                                                    total
                                                                                                                }
                                                                                                            </Typography>
                                                                                                        </Typography>
                                                                                                    }
                                                                                                />
                                                                                                <IconButton
                                                                                                    size="small"
                                                                                                    onClick={() =>
                                                                                                        toggleGroupOpen(
                                                                                                            mod.moduleKey,
                                                                                                            groupKey
                                                                                                        )
                                                                                                    }
                                                                                                >
                                                                                                    {open ? (
                                                                                                        <ExpandLessIcon />
                                                                                                    ) : (
                                                                                                        <ExpandMoreIcon />
                                                                                                    )}
                                                                                                </IconButton>
                                                                                            </Box>

                                                                                            <Collapse
                                                                                                in={
                                                                                                    open
                                                                                                }
                                                                                                timeout="auto"
                                                                                                unmountOnExit
                                                                                            >
                                                                                                <Box
                                                                                                    sx={{
                                                                                                        p: 1.5,
                                                                                                    }}
                                                                                                >
                                                                                                    <Grid container spacing={1.5}>
                                                                                                        {items.map(
                                                                                                            (
                                                                                                                s
                                                                                                            ) => (
                                                                                                                <Grid
                                                                                                                    item
                                                                                                                    xs={
                                                                                                                        12
                                                                                                                    }
                                                                                                                    sm={
                                                                                                                        6
                                                                                                                    }
                                                                                                                    md={
                                                                                                                        4
                                                                                                                    }
                                                                                                                    lg={
                                                                                                                        3
                                                                                                                    }
                                                                                                                    key={
                                                                                                                        s.submoduleKey
                                                                                                                    }
                                                                                                                >
                                                                                                                    <FormControlLabel
                                                                                                                        control={
                                                                                                                            <Checkbox
                                                                                                                                checked={isChecked(
                                                                                                                                    mod.moduleKey,
                                                                                                                                    s.submoduleKey
                                                                                                                                )}
                                                                                                                                onChange={(
                                                                                                                                    e
                                                                                                                                ) =>
                                                                                                                                    toggleSubmodule(
                                                                                                                                        mod.moduleKey,
                                                                                                                                        s.submoduleKey,
                                                                                                                                        e
                                                                                                                                            .target
                                                                                                                                            .checked
                                                                                                                                    )
                                                                                                                                }
                                                                                                                            />
                                                                                                                        }
                                                                                                                        label={
                                                                                                                            <Typography variant="body2">
                                                                                                                                {
                                                                                                                                    s.label
                                                                                                                                }
                                                                                                                            </Typography>
                                                                                                                        }
                                                                                                                    />
                                                                                                                </Grid>
                                                                                                            )
                                                                                                        )}
                                                                                                    </Grid>
                                                                                                </Box>
                                                                                            </Collapse>
                                                                                        </Box>
                                                                                    );
                                                                                }
                                                                            )}
                                                                        </Stack>
                                                                    )}
                                                                </CardContent>
                                                            </Collapse>
                                                        )}
                                                </Card>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </>
                        )}
                    </Paper>

                    {/* Barra de acciones */}
                    <Box
                        sx={{
                            position: 'sticky',
                            bottom: 0,
                            mt: 3,
                            py: 1.5,
                            backgroundColor: 'background.default',
                            borderTop: (theme) =>
                                `1px solid ${theme.palette.divider}`,
                            display: 'flex',
                            justifyContent: 'flex-end',
                            zIndex: 1,
                        }}
                    >
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={!selectedUserId || loadingPerms}
                        >
                            Guardar
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default PermissionControl;
